import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLernsets } from './LearnsetContext';
import { BookOpen, Sparkles, ArrowLeft, PlusCircle, Eye, Trash2, AlertTriangle } from 'lucide-react';

const LearnsetDashboard = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { getLernsetById, deleteLernset } = useLernsets();
    const lernset = getLernsetById(id!);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    if (!lernset) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24 text-center">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">Lernset nicht gefunden</h1>
                <Link to="/lernsets" className="text-indigo-600 hover:underline">
                    Zurück zu meinen Lernsets
                </Link>
            </div>
        );
    }

    const handleDelete = async () => {
        try {
            await deleteLernset(id!);
            navigate('/lernsets');
        } catch (error) {
            console.error('Fehler beim Löschen:', error);
            alert('Löschen fehlgeschlagen. Bitte versuche es später erneut.');
        }
    };

    const actions = [
        {
            title: 'Crashmodus',
            description: 'Alle Karten in einer Session durchlernen',
            icon: BookOpen,
            color: 'from-green-500 to-emerald-500',
            link: 'learn/crash',
        },
        {
            title: 'Tägliche Session',
            description: 'In Kürze: Verteiltes Lernen über mehrere Tage',
            icon: Sparkles,
            color: 'from-purple-500 to-pink-500',
            link: 'learn/daily',
        },
        {
            title: 'KI-generieren',
            description: 'Lass KI aus Texten Karteikarten erstellen',
            icon: Sparkles,
            color: 'from-purple-500 to-pink-500',
            link: 'generate',
        },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24">
            <div className="max-w-6xl mx-auto">
                <div className="mb-8">
                    <div className="flex justify-between items-start">
                        <div>
                            <Link
                                to="/lernsets"
                                className="inline-flex items-center text-gray-600 hover:text-indigo-600 mb-4 transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4 mr-1" />
                                Alle Lernsets
                            </Link>
                            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                                {lernset.name}
                            </h1>
                            {lernset.description && (
                                <p className="text-gray-600 text-lg">{lernset.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                                <span>{lernset.cards.length} Karteikarten</span>
                                <span>·</span>
                                <span>Erstellt am {new Date(lernset.createdAt).toLocaleDateString('de-DE')}</span>
                            </div>
                        </div>
                        {/* 🔥 Löschen-Button */}
                        <button
                            onClick={() => setShowDeleteModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors border border-red-200"
                        >
                            <Trash2 className="w-4 h-4" />
                            Lernset löschen
                        </button>
                    </div>
                </div>

                <div className="mb-12">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold text-gray-800">Karteikarten</h2>
                        <div className="flex items-center gap-3">
                            <Link
                                to={`/lernset/${id}/cards`}
                                className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 text-sm font-medium"
                            >
                                <Eye className="w-4 h-4" />
                                Alle anzeigen / bearbeiten
                            </Link>
                            <Link
                                to={`/lernset/${id}/create`}
                                className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 text-sm font-medium"
                            >
                                <PlusCircle className="w-4 h-4" />
                                Neu
                            </Link>
                        </div>
                    </div>
                    {lernset.cards.length === 0 ? (
                        <div className="bg-white/50 rounded-2xl p-8 text-center border border-dashed border-gray-300">
                            <p className="text-gray-500">Noch keine Karteikarten vorhanden.</p>
                            <p className="text-gray-400 text-sm mt-1">
                                Erstelle die ersten Karten manuell oder per KI.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {lernset.cards.slice(0, 5).map((card) => (
                                <div
                                    key={card.id}
                                    className="bg-white/70 rounded-xl p-4 flex justify-between items-center border border-gray-100"
                                >
                                    <p className="text-gray-800 truncate max-w-md">{card.question}</p>
                                    <span className="text-xs text-gray-400">Antwort: {card.answer.substring(0, 30)}…</span>
                                </div>
                            ))}
                            {lernset.cards.length > 5 && (
                                <p className="text-sm text-gray-500 mt-2">
                                    und {lernset.cards.length - 5} weitere...
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <h2 className="text-xl font-semibold text-gray-800 mb-4">Aktionen</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {actions.map((action, index) => (
                        <motion.div
                            key={action.title}
                            whileHover={{ y: -4 }}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <Link
                                to={`/lernset/${id}/${action.link}`}
                                className="block h-full bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/50 p-6 hover:shadow-2xl transition-all"
                            >
                                <div
                                    className={`w-12 h-12 mb-4 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center shadow-md`}
                                >
                                    <action.icon className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    {action.title}
                                </h3>
                                <p className="text-gray-600 text-sm">{action.description}</p>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* 🔥 Lösch-Bestätigungsmodal */}
            <AnimatePresence>
                {showDeleteModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        onClick={() => setShowDeleteModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 max-w-md w-full border border-white/50"
                        >
                            <div className="flex items-center gap-3 text-red-600 mb-4">
                                <AlertTriangle className="w-8 h-8" />
                                <h2 className="text-2xl font-bold">Lernset löschen?</h2>
                            </div>
                            <p className="text-gray-700 mb-6">
                                Möchtest du das Lernset <span className="font-semibold">"{lernset.name}"</span> wirklich löschen?
                                Alle darin enthaltenen Karteikarten werden unwiderruflich gelöscht.
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowDeleteModal(false)}
                                    className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                                >
                                    Abbrechen
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="flex-1 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors shadow-lg"
                                >
                                    Löschen
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default LearnsetDashboard;